/**
 * Minimal type for the Customer.io analytics stub — just enough for the factory.
 * The full CustomerIOAnalytics interface lives in customerio.ts.
 */
export interface AnalyticsStub {
  push: (args: unknown[]) => void;
  [key: string]: unknown;
}

/**
 * Creates a guarded factory that produces method wrappers for the analytics stub.
 * Each wrapper queues a `[method, ...args]` tuple via `analytics.push()`.
 *
 * A recursion guard prevents the infinite loop that occurs when the real SDK
 * loads and its `push()` drains the queue: SDK push → factory wrapper → push → …
 * (See KIRIN-FRONTEND-7T / KIR-2205)
 */
export function createAnalyticsMethodFactory<T extends AnalyticsStub>(
  analytics: T
) {
  let factoryCallInProgress = false;

  return function factory(method: string) {
    return function (...args: unknown[]): T {
      if (factoryCallInProgress) return analytics;
      factoryCallInProgress = true;
      try {
        const t = Array.prototype.slice.call(args);
        t.unshift(method);
        analytics.push(t);
      } finally {
        factoryCallInProgress = false;
      }
      return analytics;
    };
  };
}
